import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput, { formatFractionLatexCore } from "../../../../components/inputs/FractionInput";

/** ===== UŁAMKI ===== */
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) [a, b] = [b, a % b]; return a || 1; }
function normFrac(fr) {
  if (!fr || fr.denominator == null || fr.numerator == null || fr.denominator === 0) {
    return { numerator: NaN, denominator: NaN };
  }
  const sign = fr.denominator < 0 ? -1 : 1;
  const n = fr.numerator * sign;
  const d = Math.abs(fr.denominator);
  const g = gcd(n, d);
  return { numerator: n / g, denominator: d / g };
}
function equalsFrac(a, b) { const A = normFrac(a), B = normFrac(b); return A.numerator === B.numerator && A.denominator === B.denominator; }

function toLatex(fr) {
  const f = normFrac(fr);
  if (Number.isNaN(f.numerator) || Number.isNaN(f.denominator)) return "\\text{?}";
  const sign = f.numerator < 0 ? "-" : "";
  const num = Math.abs(f.numerator);
  const den = f.denominator;
  return den === 1 ? `${sign}${num}` : `${sign}\\dfrac{${num}}{${den}}`;
}

/** ===== ŁADNE DANE ===== */
const TRIPLES = [
  [3, 4, 5],
  [5, 12, 13],
  [8, 15, 17],
  [7, 24, 25],
  [20, 21, 29],
  [12, 35, 37],
];

export const type = "GivenTanFindSinCosTask";

/**
 * variant: "I" | "II"
 *  I   ćwiartka:  sin > 0, cos > 0, tan > 0
 *  II  ćwiartka:  sin > 0, cos < 0, tan < 0
 */
export function generateTask({ variant = "I" } = {}) {
  const [a, b, c] = TRIPLES[Math.floor(Math.random() * TRIPLES.length)];
  // Kąt przy boku 'a' lub 'b'
  const angleAt = Math.random() < 0.5 ? "a" : "b";

  // Dla kąta przy 'a': tan = a/b, sin = a/c, cos = b/c
  // Dla kąta przy 'b': tan = b/a, sin = b/c, cos = a/c
  let sinPos, cosPos, tanPos;
  if (angleAt === "a") {
    sinPos = { numerator: a, denominator: c };
    cosPos = { numerator: b, denominator: c };
    tanPos = { numerator: a, denominator: b };
  } else {
    sinPos = { numerator: b, denominator: c };
    cosPos = { numerator: a, denominator: c };
    tanPos = { numerator: b, denominator: a };
  }

  const sin = normFrac({ numerator: +sinPos.numerator, denominator: sinPos.denominator }); // I i II: dodatni
  const cos = normFrac({ numerator: (variant === "I" ? +cosPos.numerator : -cosPos.numerator), denominator: cosPos.denominator });
  const tan = normFrac({ numerator: (variant === "I" ? +tanPos.numerator : -tanPos.numerator), denominator: tanPos.denominator });

  return {
    type,
    data: { variant, a, b, c, angleAt, given: { tan }, expected: { sin, cos } }
  };
}

export function renderTask({ data }) {
  const { variant, given: { tan } } = data;
  const range = variant === "I" ? "(0^\\circ, 90^\\circ)" : "(90^\\circ, 180^\\circ)";
  return (
    <div style={{ marginBottom: "0.5rem" }}>
      Dany <InlineMath math={'\\tan\\alpha'} /> = <InlineMath math={toLatex(tan)} />, oblicz{" "}
      <InlineMath math={'\\sin\\alpha'} /> oraz <InlineMath math={'\\cos\\alpha'} />.
      <div style={{ marginTop: "0.5rem" }}>
        Zakres kąta: <InlineMath math={range} />
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ans = userAnswer ?? {};
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <InlineMath math={'\\sin\\alpha ='} />
        <FractionInput
          value={ans.sin}
          onChange={(v) => onChange({ ...ans, sin: v })}
          disabled={disabled}
          label={'\\sin\\alpha'}
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={'\\cos\\alpha ='} />
        <FractionInput
          value={ans.cos}
          onChange={(v) => onChange({ ...ans, cos: v })}
          disabled={disabled}
          label={'\\cos\\alpha'}
        />
      </div>
    </div>
  );
}

/** ===== Parsowanie odpowiedzi i sprawdzanie ===== */
export function checkAnswer({ data }, userAnswer) {
  const exp = data.expected;

  const isBlankInput = (v) => {
    if (v == null) return true;
    if (typeof v === "object") {
      if (v.numerator != null && v.denominator != null) return false;
      const raw = v.raw ?? v.value ?? null;
      const s = String(raw ?? "").trim().toLowerCase();
      return s === "" || s === "null" || s === "undefined" || s === "-" || s === "+";
    }
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      return s === "" || s === "null" || s === "undefined";
    }
    if (typeof v === "number") return Number.isNaN(v);
    return false;
  };

  if (!userAnswer || isBlankInput(userAnswer.sin) || isBlankInput(userAnswer.cos)) {
    return {
      correct: false,
      message: "Uzupełnij obie wartości.",
      score: 0,
      maxScore: 2,
      parts: { "sinα": false, "cosα": false },
      isCorrect: false,
    };
  }

  const TOL = 0.01;

  const toNumber = (f) => {
    if (!f || f.numerator == null || f.denominator == null || f.denominator === 0) return NaN;
    return f.numerator / f.denominator;
  };

  const parseDecimal = (raw) => {
    if (raw == null) return null;
    let s = String(raw).trim().replace(/\u2212/g, "-").replace(",", ".");
    if (/^\(.*\)$/.test(s)) s = s.slice(1, -1).trim();
    if (/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(s)) return parseFloat(s);
    return null;
  };

  const parseFraction = (val) => {
    if (val && typeof val === "object" && val.numerator != null && val.denominator != null) return val;

    const raw = typeof val === "object" ? val.raw : val;
    if (raw == null) return val;

    const s = String(raw).trim().replace(/\u2212/g, "-");

    let m = s.match(/^([+-])?\s*(\d+)\s+(\d+)\s*\/\s*([+-]?\d+)$/);
    if (m) {
      const lead = m[1] === "-" ? -1 : 1;
      const I = parseInt(m[2], 10);
      const N = parseInt(m[3], 10);
      const D = parseInt(m[4], 10);
      const num = lead * (I * Math.abs(D) + N) * (D < 0 ? -1 : 1);
      const den = Math.abs(D);
      return { numerator: num, denominator: den };
    }

    m = s.match(/^([+-])?\s*\(?\s*([+-]?\d+)\s*\)?\s*\/\s*([+-]?\d+)$/);
    if (m) {
      const lead = m[1] === "-" ? -1 : 1;
      const N = parseInt(m[2], 10);
      const D = parseInt(m[3], 10);
      return { numerator: lead * N, denominator: D };
    }

    m = s.match(/^([+-]?\d+)$/);
    if (m) return { numerator: parseInt(m[1], 10), denominator: 1 };

    return val;
  };

  const isCorrectOne = (userVal, expectedFrac) => {
    const frac = parseFraction(userVal);
    if (frac && typeof frac === "object" && frac.numerator != null && frac.denominator != null) {
      return equalsFrac(frac, expectedFrac);
    }
    const raw = typeof userVal === "object" ? userVal.raw : userVal;
    const num = parseDecimal(raw);
    if (num == null) return false;

    const expectedNum = toNumber(expectedFrac);
    return Number.isFinite(expectedNum) && Math.abs(num - expectedNum) <= TOL;
  };

  const okSin = isCorrectOne(userAnswer.sin, exp.sin);
  const okCos = isCorrectOne(userAnswer.cos, exp.cos);

  const score = (okSin ? 1 : 0) + (okCos ? 1 : 0);
  const maxScore = 2;
  const parts = { "sinα": okSin, "cosα": okCos };

  let message = "Świetnie!";
  if (score !== 2) {
    if (score === 0) message = "Błędne obie wartości. Sprawdź znak i skrócenie.";
    if (score === 1 && !okSin) message = "Dobrze \\(\\cos\\alpha\\), ale sprawdź \\(\\sin\\alpha\\) (znak/skrócenie).";
    if (score === 1 && !okCos) message = "Dobrze \\(\\sin\\alpha\\), ale sprawdź \\(\\cos\\alpha\\) (znak/skrócenie).";
  }

  return {
    correct: score === 2,
    message,
    score,
    maxScore,
    parts,
    isCorrect: score === 2,
  };
}


export function renderAnswer({ data }) {
  const { expected: { sin, cos } } = data;
  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <InlineMath math={`\\sin\\alpha = ${toLatex(sin)}`} />
      </div>
      <div>
        <InlineMath math={`\\cos\\alpha = ${toLatex(cos)}`} />
      </div>
    </div>
  );
}

/**
 * Wyjaśnienie „z układu”:
 * tan = sin/cos oraz sin^2 + cos^2 = 1.
 * Z proporcji (|num| : |den|) normalizujemy przez √(num^2 + den^2), a znak dobieramy wg ćwiartki.
 */
export function renderExplanation({ data }) {
  const {
    variant,
    a, b, c,                // mamy c do uproszczeń
    given: { tan },
    expected: { sin, cos }
  } = data;

  // podpisany tangens do podstawień
  const num = tan.numerator;
  const den = tan.denominator;

  // kwadraty do wspólnego mianownika (znak tu znika)
  const p2 = Math.abs(num) * Math.abs(num);
  const q2 = Math.abs(den) * Math.abs(den);

  const wrap = (x) => `{${x}}`;

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem", lineHeight: 1.8 }}>
      {/* Krok 1 */}
      <div style={{ marginBottom: "0.25rem" }}>
        <strong>Krok 1:</strong> Z definicji tangensa i wymnożenia przez mianownik
      </div>
      <BlockMath math={`\\tan\\alpha = \\dfrac{\\sin\\alpha}{\\cos\\alpha}`} />
      <BlockMath math={`\\Rightarrow\\; \\sin\\alpha = \\tan\\alpha\\,\\cos\\alpha`} />
      <div style={{ marginTop: "0.25rem" }}>
        Dla <InlineMath math={`\\tan\\alpha = ${toLatex(tan)}`} /> mamy:
      </div>
      <BlockMath math={`\\sin\\alpha = (${toLatex(tan)})\\,\\cos\\alpha`} />

      {/* Krok 2 */}
      <div style={{ marginTop: "0.75rem", marginBottom: "0.25rem" }}>
        <strong>Krok 2:</strong> Podstawiamy do jedynki trygonometrycznej
      </div>
      <BlockMath math={`\\sin^2\\alpha + \\cos^2\\alpha = 1`} />
      <BlockMath math={`\\big((${toLatex(tan)})\\,\\cos\\alpha\\big)^2 + \\cos^2\\alpha = 1`} />
      <BlockMath math={`\\dfrac{${p2}}{${q2}}\\cos^2\\alpha + \\dfrac{${q2}}{${q2}}\\cos^2\\alpha = 1`} />
      <BlockMath math={`\\dfrac{${p2 + q2}}{${q2}}\\cos^2\\alpha - 1 = 0`} />

      {/* Krok 3 */}
      <div style={{ marginTop: "0.75rem", marginBottom: "0.25rem" }}>
        <strong>Krok 3:</strong> Rozwiązujemy równanie dla <InlineMath math={'\\cos\\alpha'} />
      </div>
      <BlockMath math={`\\dfrac{${p2 + q2}}{${q2}}\\cos^2\\alpha = 1`} />
      <BlockMath math={`\\cos^2\\alpha = \\dfrac{${q2}}{${p2 + q2}}`} />
      <BlockMath math={`|\\cos\\alpha| = \\dfrac{${Math.abs(den)}}{${c}}`} />
      <div>W ćwiartce {variant} znak daje:</div>
      <BlockMath math={`\\cos\\alpha = ${toLatex(cos)}`} />

      {/* Krok 4 */}
      <div style={{ marginTop: "0.5rem", marginBottom: "0.25rem" }}>
        <strong>Krok 4:</strong> Wracamy do <InlineMath math={'\\sin\\alpha = \\tan\\alpha\\,\\cos\\alpha'} />
      </div>
      <BlockMath math={`\\sin\\alpha = (${toLatex(tan)})\\cdot ${wrap(toLatex(cos))} = ${toLatex(sin)}`} />
    </div>
  );
}







export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const isBlankInput = (v) => {
    if (v == null) return true;
    if (typeof v === "object") {
      if (v.numerator != null && v.denominator != null) return false;
      const raw = v.raw ?? v.value ?? null;
      const s = String(raw ?? "").trim().toLowerCase();
      return s === "" || s === "null" || s === "undefined" || s === "-" || s === "+";
    }
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      return s === "" || s === "null" || s === "undefined";
    }
    if (typeof v === "number") return Number.isNaN(v);
    return false;
  };

  const sanitizeRaw = (raw) => {
    const s = String(raw ?? "").trim();
    const low = s.toLowerCase();
    if (low === "null" || low === "undefined") return "";
    return s;
  };

  const sinBlank = isBlankInput(userAnswer.sin);
  const cosBlank = isBlankInput(userAnswer.cos);

  if (sinBlank && cosBlank) return <i>(brak odpowiedzi)</i>;

  const sinRaw = typeof userAnswer.sin === "object" ? userAnswer.sin.raw : userAnswer.sin;
  const cosRaw = typeof userAnswer.cos === "object" ? userAnswer.cos.raw : userAnswer.cos;

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <InlineMath math={`\\displaystyle \\sin\\alpha = ${formatFractionLatexCore(sanitizeRaw(sinRaw))}`} />
      </div>
      <div>
        <InlineMath math={`\\displaystyle \\cos\\alpha = ${formatFractionLatexCore(sanitizeRaw(cosRaw))}`} />
      </div>
    </div>
  );
}
